// src/pages/Feed.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import pb from '../lib/pb';
import PostCard, { Avatar, PostRail, getMediaItems, injectStyles, setRailOutside, useMfTheme } from '../shared/PostCard';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import FriendsPanel from '../components/FriendsPanel';
import SearchBarWithMaya from '../components/SearchBarWithMaya';
import { useAuth } from '../contexts/AuthContext';
import SegmentTabs from '../components/SegmentTabs';
import PopularChallenges from '../components/PopularChallenges';
import BuzzingChallenges from '../components/BuzzingChallenges';
import StickyFooter from "../components/StickyFooter";
import WelcomePopup from "../components/WelcomePopup";
import { getMediaUrl, getPbUrl } from '../lib/media';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

/* ------------------------------------------------------------------ */
/* Config                                                              */
/* ------------------------------------------------------------------ */
const LOGO_SRC = '/logo.jpeg';            // put your logo in /public (rounded square is applied by CSS)
const MOBILE_QUERY = '(max-width: 767px)';
const WIDE_QUERY = '(min-width: 1200px)'; // 3 columns: Friends | Feed | Party

const FRIENDS_W = 300;         // expanded width of the Friends column (px)
const PARTY_W = 340;           // expanded width of the Party column (px)
const RAIL_W = 64;             // width of a collapsed column (px)

const PARTY_FETCH = 20;        // how many party items are loaded per section
const PANEL_MAX = 10;          // desktop / tablet panel shows up to this many (as before)

const STRIP_FIRST = 4;         // phone strip: items shown at first
const STRIP_STEP = 4;          // phone strip: extra items revealed each time its end is reached
const STRIP_MAX = 20;          // phone strip: never more than this
const STRIP_HIDE_AT = 24;      // px of page scroll after which the strip slides away
const STRIP_REAPPEARS = true;  // true: slides back in when the page is scrolled to the top again
                               // false: shown only once, on first load

/* ------------------------------------------------------------------ */
/* Styles (theme tokens + shared buttons live in PostCard.js)           */
/* ------------------------------------------------------------------ */
const FEED_CSS = `
.page.feed.mf-feed{
  --mf-pad:clamp(12px,2.2vw,32px);
  --mf-strip-w:68px;
  margin:0; padding:0; max-width:none; width:100%; min-height:100vh;
  background:radial-gradient(1000px 380px at 50% -140px,var(--mf-glow),transparent 70%),var(--mf-bg);
  color:var(--mf-text);
}

/* ---------- Top bar ---------- */
.mf-topbar{position:sticky;top:0;z-index:50;background:var(--mf-bg-top);border-bottom:1px solid var(--mf-border)}
.mf-topbar-inner{width:100%;margin:0;padding:10px var(--mf-pad);display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.mf-brand{margin:0;flex:1 1 auto;min-width:0;font-size:22px;line-height:1;letter-spacing:-.02em}
.mf-brand a{display:inline-flex;align-items:center;gap:10px}
.mf-logo{width:40px;height:40px;border-radius:12px;object-fit:cover;flex:none;background:var(--mf-surface-2);
  box-shadow:0 0 0 1px var(--mf-border-strong),0 6px 16px rgba(0,0,0,.25)}
.mf-logo-fallback{display:grid;place-items:center;background:var(--mf-grad);color:#fff;font-weight:800;font-size:22px;border-radius:12px}
.mf-wordmark{font-weight:500;color:var(--mf-text)}
.mf-wordmark b{font-weight:800;color:var(--mf-text)}
.mf-create-wrap{display:flex}
.mf-feed .mf-create{display:inline-flex;align-items:center;gap:4px;height:38px;padding:0 16px 0 12px;border-radius:999px;
  background:var(--mf-grad);color:#fff;font-weight:700;font-size:14px;box-shadow:0 6px 16px rgba(0,0,0,.28);
  transition:transform .15s,filter .15s}
.mf-feed .mf-create:hover{filter:brightness(1.07);transform:translateY(-1px)}
.mf-create svg{font-size:20px}

/* ---------- Search + widgets ---------- */
.mf-search{width:100%;padding:16px var(--mf-pad) 4px}
.mf-search input{background:var(--mf-surface);color:var(--mf-text);border:1px solid var(--mf-border-strong);
  border-radius:14px;font-size:16px;outline:none}
.mf-search input::placeholder{color:var(--mf-faint)}
.mf-search input:focus{border-color:var(--mf-accent);box-shadow:0 0 0 3px var(--mf-accent-soft)}
.mf-widgets{width:100%;padding:10px var(--mf-pad) 0;display:flex;flex-direction:column;gap:14px}

/* category tabs: centred on web screens */
.mf-segwrap{width:100%;min-width:0}
@media (min-width:768px){
  .mf-segwrap{display:flex;justify-content:center;text-align:center}
  .mf-segwrap>*{margin-left:auto;margin-right:auto;max-width:100%}
  .mf-segwrap [class*="segment" i],.mf-segwrap [class*="tabs" i]{justify-content:center}
}

/* ---------- Layout ----------
   < 1200px : single column  (Friends card, then Party card, then the feed)
   >= 1200px: Friends (left) | Feed (middle) | Party (right); each side column can
              collapse into a slim rail. Only this grid (everything BELOW the Buzzing
              widgets) reacts to the panels; the search bar and widgets above never change. */
.mf-layout{width:100%;padding:16px var(--mf-pad) 100px;display:grid;grid-template-columns:minmax(0,1fr);gap:20px;align-items:start}
.mf-main{display:flex;flex-direction:column;gap:16px;min-width:0;width:100%}
@media (min-width:1200px){
  .mf-layout{grid-template-columns:var(--mf-left-w,300px) minmax(0,1fr) var(--mf-right-w,340px);gap:24px;transition:grid-template-columns .25s ease}
}

/* ---------- Friends panel column ---------- */
.mf-friends{min-width:0}
@media (min-width:1200px){
  .mf-friends{position:sticky;top:84px;display:flex;flex-direction:column;max-height:calc(100vh - 104px)}
  .mf-friends .fr-card{flex:1;min-height:0}
}
@media (max-width:1199px){
  .mf-friends{order:-2}
  .mf-friends .fr-body{max-height:min(70vh,560px)}
}

/* ---------- Section heading / results / empty / skeleton ---------- */
.mf-section-head{display:flex;align-items:center;justify-content:space-between;gap:12px}
.mf-section-head h2{margin:0;font-size:18px;font-weight:800}
.mf-sub{margin:10px 0 0;font-size:12px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:var(--mf-faint)}
.mf-results{display:flex;flex-direction:column;gap:12px}
.mf-user-row{display:flex;align-items:center;gap:12px;padding:10px 14px;border-radius:14px;background:var(--mf-surface);
  border:1px solid var(--mf-border);transition:background-color .15s,border-color .15s}
.mf-user-row:hover{background:var(--mf-surface-2);border-color:var(--mf-border-strong)}
.mf-user-row strong{display:block;font-size:15px}
.mf-user-row span.mf-handle{font-size:13px;color:var(--mf-faint)}

.mf-empty{text-align:center;padding:48px 24px;background:var(--mf-surface);border:1px dashed var(--mf-border-strong);border-radius:18px;color:var(--mf-muted)}
.mf-empty h3{margin:14px 0 6px;color:var(--mf-text);font-size:18px}
.mf-empty p{margin:0 0 16px;font-size:14.5px}
.mf-empty-icon{width:56px;height:56px;border-radius:16px;margin:0 auto;display:grid;place-items:center;background:var(--mf-accent-soft);color:var(--mf-accent);font-size:26px}
.mf-empty--error{border-style:solid;border-color:var(--mf-accent)}

.mf-skel{background:var(--mf-surface);border:1px solid var(--mf-border);border-radius:18px;padding:16px;display:flex;flex-direction:column;gap:12px}
.mf-skel-row{display:flex;gap:12px;align-items:center}
.mf-sk{border-radius:10px;background:linear-gradient(90deg,var(--mf-surface-2) 25%,var(--mf-surface-3) 37%,var(--mf-surface-2) 63%);
  background-size:400% 100%;animation:mf-shimmer 1.4s ease infinite}
@keyframes mf-shimmer{0%{background-position:100% 50%}100%{background-position:0 50%}}

/* ---------- Party panel (desktop + tablet) ---------- */
.mf-party{background:var(--mf-surface);border:1px solid var(--mf-border);border-radius:18px;box-shadow:var(--mf-shadow);overflow:hidden;min-width:0}
.mf-party-head{display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;padding:14px 16px;border-bottom:1px solid var(--mf-border)}
.mf-party-title{margin:0;font-size:16px;font-weight:800;display:flex;align-items:center;gap:8px}
.mf-count{font-size:12px;font-weight:800;padding:2px 8px;border-radius:999px;background:var(--mf-accent-soft);color:var(--mf-accent)}
.mf-tabs{display:flex;background:var(--mf-surface-2);border-radius:999px;padding:3px;gap:2px}
.mf-tab{border:0;background:transparent;color:var(--mf-muted);font-weight:700;font-size:12.5px;padding:7px 12px;border-radius:999px;cursor:pointer;transition:background-color .15s,color .15s}
.mf-tab:hover{color:var(--mf-text)}
.mf-tab.is-active{background:var(--mf-tab-active);color:var(--mf-text);box-shadow:0 1px 3px rgba(0,0,0,.22)}
.mf-party-toggle{display:none;width:36px;height:36px;border-radius:10px;border:0;background:var(--mf-surface-2);color:var(--mf-muted);place-items:center;cursor:pointer}
.mf-party-toggle svg{transition:transform .2s}
.mf-party:not(.is-collapsed) .mf-party-toggle svg{transform:rotate(180deg)}
.mf-iconbtn{display:inline-grid;place-items:center;width:36px;height:36px;border-radius:10px;border:0;background:var(--mf-surface-2);color:var(--mf-muted);cursor:pointer;flex:none;
  transition:background-color .15s,color .15s}
.mf-iconbtn:hover{background:var(--mf-surface-3);color:var(--mf-text)}
.mf-party-body{padding:14px}
.mf-party-list{display:flex;flex-direction:column;gap:12px}
.mf-party-note{margin:0;text-align:center;color:var(--mf-faint);font-size:14px;padding:18px 0}

/* collapsed Party rail (desktop) */
.mf-party-rail{display:flex;flex-direction:column;align-items:center;gap:14px;width:100%;padding:12px 0 18px;background:var(--mf-surface);
  border:1px solid var(--mf-border);border-radius:18px;box-shadow:var(--mf-shadow);color:var(--mf-text)}
.mf-rail-ico{position:relative;width:36px;height:36px;border-radius:12px;background:var(--mf-grad);color:#fff;display:grid;place-items:center;font-size:18px}
.mf-rail-badge{position:absolute;top:-6px;right:-8px;min-width:18px;height:18px;padding:0 5px;border-radius:999px;background:var(--mf-accent);color:#fff;
  font-size:11px;font-weight:800;display:grid;place-items:center;box-shadow:0 0 0 2px var(--mf-surface)}
.mf-rail-label{writing-mode:vertical-rl;font-size:12px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:var(--mf-muted)}
.mf-party-rail{scrollbar-width:none}
.mf-party-rail::-webkit-scrollbar{display:none}

.mf-party-card{background:var(--mf-surface-2);border:1px solid var(--mf-border);border-radius:14px;overflow:hidden;display:flex;flex-direction:column;min-width:0}
.mf-party-media{aspect-ratio:16/9;background:var(--mf-surface-3);display:grid;place-items:center;color:var(--mf-faint);font-weight:800;letter-spacing:.14em;font-size:12px}
.mf-party-media img{width:100%;height:100%;object-fit:cover;display:block}
.mf-party-info{padding:12px 14px 14px;display:flex;flex-direction:column;gap:8px;min-width:0}
.mf-party-info h3{margin:0;font-size:15px;font-weight:700;line-height:1.3}
.mf-party-info p{margin:0;color:var(--mf-muted);font-size:13.5px;line-height:1.45;overflow-wrap:anywhere}
.mf-meta-list{display:grid;gap:4px;font-size:13px;color:var(--mf-muted)}
.mf-chips{display:flex;flex-wrap:wrap;gap:6px}
.mf-chip{font-size:12px;font-weight:600;padding:4px 9px;border-radius:999px;background:var(--mf-surface);border:1px solid var(--mf-border);color:var(--mf-muted)}
.mf-party-actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:2px}
.mf-viewall{width:100%}

@media (min-width:1200px){
  .mf-party,.mf-party-rail{position:sticky;top:84px}
  .mf-party-rail{max-height:calc(100vh - 104px);overflow-y:auto;overscroll-behavior:contain}
  .mf-party-body{max-height:calc(100vh - 230px);overflow-y:auto;overscroll-behavior:contain}
}
@media (max-width:1199px){
  .mf-party{order:-1}
  .mf-party-toggle{display:inline-grid}
  .mf-party.is-collapsed .mf-party-body{display:none}
  .mf-party.is-collapsed .mf-party-head{border-bottom:0}
  .mf-party-list{flex-direction:row;overflow-x:auto;scroll-snap-type:x proximity;padding-bottom:6px;-webkit-overflow-scrolling:touch}
  .mf-party-list>*{flex:0 0 min(84%,320px);scroll-snap-align:start}
  .mf-viewall{align-self:center}
}

/* ---------- Party strip (phones only) ----------
   It floats over the right edge only while the page is at the top; as soon as
   the feed is scrolled it slides away, so posts always use the full width.
   Its top (--mf-strip-top) is measured in JS: right under the category tabs. */
.mf-strip{position:fixed;z-index:40;right:max(8px,env(safe-area-inset-right));top:var(--mf-strip-top,150px);width:var(--mf-strip-w);
  max-height:max(180px,min(560px,calc(100vh - var(--mf-strip-top,150px) - 100px)));
  display:flex;flex-direction:column;gap:8px;overflow-y:auto;overscroll-behavior:contain;scrollbar-width:none;-webkit-overflow-scrolling:touch;
  transition:transform .28s cubic-bezier(.4,0,.2,1),opacity .2s ease,visibility 0s}
@supports (height:100svh){
  .mf-strip{max-height:max(180px,min(560px,calc(100svh - var(--mf-strip-top,150px) - 100px)))}
}
.mf-strip::-webkit-scrollbar{display:none}
.mf-strip.is-away{transform:translateX(calc(100% + 16px));opacity:0;pointer-events:none;visibility:hidden;
  transition:transform .28s cubic-bezier(.4,0,.2,1),opacity .2s ease,visibility 0s .28s}
.mf-strip-tabs{position:sticky;top:0;z-index:2;flex:none;display:flex;gap:2px;padding:3px;border-radius:999px;background:var(--mf-surface-2);
  border:1px solid var(--mf-border);box-shadow:0 4px 12px rgba(0,0,0,.28)}
.mf-strip-tabs button{flex:1;height:30px;border:0;border-radius:999px;background:transparent;font-size:15px;line-height:1;cursor:pointer;opacity:.55}
.mf-strip-tabs button.is-active{background:var(--mf-tab-active);opacity:1;box-shadow:0 1px 3px rgba(0,0,0,.25)}
.mf-strip-empty{margin:8px 0;text-align:center;font-size:11px;color:var(--mf-faint)}
.mf-strip-card{position:relative;flex:none;display:block;width:100%;aspect-ratio:9/16;padding:2px;border:0;border-radius:16px;background:var(--mf-grad);
  cursor:pointer;text-align:left;box-shadow:0 6px 18px rgba(0,0,0,.35);-webkit-tap-highlight-color:transparent;transition:transform .12s}
.mf-strip-card:active{transform:scale(.97)}
.mf-strip-inner{position:absolute;inset:2px;display:block;border-radius:14px;overflow:hidden;background:var(--mf-surface-3)}
.mf-strip-inner img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block}
.mf-strip-inner::after{content:'';position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,.84) 0%,rgba(0,0,0,0) 56%)}
.mf-strip-ph{position:absolute;inset:0;display:grid;place-items:center;font-size:24px}
.mf-strip-live{position:absolute;top:6px;left:6px;z-index:2;display:flex;align-items:center;gap:3px;padding:2px 6px;border-radius:999px;
  background:rgba(0,0,0,.62);color:#fff;font-size:8.5px;font-weight:800;letter-spacing:.06em}
.mf-strip-live i{width:6px;height:6px;border-radius:50%;background:#ff3b3b;display:block}
.mf-strip-ava{position:absolute;left:50%;bottom:32px;transform:translateX(-50%);z-index:2;width:24px;height:24px;border-radius:50%;display:grid;place-items:center;
  background:var(--mf-surface);color:var(--mf-text);font-size:11px;font-weight:800;box-shadow:0 0 0 2px #fff}
.mf-strip-label{position:absolute;left:5px;right:5px;bottom:6px;z-index:2;color:#fff;font-size:9.5px;font-weight:700;line-height:1.2;text-align:center;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:anywhere}
.mf-strip-more{aspect-ratio:9/10}
.mf-strip-more .mf-strip-inner{display:grid;place-content:center;justify-items:center;gap:4px;background:var(--mf-surface);color:var(--mf-text);
  font-size:10px;font-weight:800;text-align:center}
.mf-strip-more .mf-strip-inner::after{display:none}
.mf-strip-more svg{font-size:22px;color:var(--mf-accent)}

/* ---------- Bottom sheets (party card tap + friends) ---------- */
.mf-sheet-overlay{position:fixed;inset:0;z-index:1100;background:var(--mf-overlay);display:flex;align-items:flex-end;justify-content:center}
.mf-sheet{width:100%;max-width:560px;max-height:88vh;display:flex;flex-direction:column;background:var(--mf-surface);border:1px solid var(--mf-border-strong);
  border-bottom:0;border-radius:22px 22px 0 0;box-shadow:0 -18px 50px rgba(0,0,0,.45);animation:mf-sheet-up .22s ease-out;color:var(--mf-text)}
@keyframes mf-sheet-up{from{transform:translateY(24px);opacity:0}to{transform:none;opacity:1}}
.mf-sheet-head{position:relative;flex:none;padding:12px 12px 6px}
.mf-sheet-grip{display:block;width:44px;height:5px;border-radius:999px;background:var(--mf-border-strong);margin:0 auto}
.mf-sheet-x{position:absolute;right:10px;top:6px;width:36px;height:36px;border-radius:10px;border:0;background:transparent;color:var(--mf-muted);display:grid;place-items:center;cursor:pointer}
.mf-sheet-x:hover{background:var(--mf-surface-2);color:var(--mf-text)}
.mf-sheet-body{overflow-y:auto;padding:8px 14px calc(18px + env(safe-area-inset-bottom))}

/* Friends sheet: always mounted on phones (keeps requests + notifications live),
   only displayed while open */
.mf-fsheet-overlay{position:fixed;inset:0;z-index:1100;background:var(--mf-overlay);display:none;align-items:flex-end;justify-content:center}
.mf-fsheet-overlay.is-open{display:flex}
.mf-fsheet{width:100%;max-width:560px;height:min(88vh,760px);display:flex;flex-direction:column;overflow:hidden;background:var(--mf-surface);
  border:1px solid var(--mf-border-strong);border-bottom:0;border-radius:22px 22px 0 0;box-shadow:0 -18px 50px rgba(0,0,0,.45);
  animation:mf-sheet-up .22s ease-out;color:var(--mf-text)}
.mf-fsheet-grip{flex:none;display:block;width:44px;height:5px;border-radius:999px;background:var(--mf-border-strong);margin:10px auto 0}
.mf-fsheet .fr-card{flex:1;min-height:0;border:0;border-radius:0;box-shadow:none;background:transparent}
.mf-fsheet .fr-body{padding-bottom:calc(18px + env(safe-area-inset-bottom))}

.mf-fab{position:fixed;z-index:45;left:max(12px,env(safe-area-inset-left));bottom:calc(88px + env(safe-area-inset-bottom));
  width:52px;height:52px;border-radius:50%;border:0;background:var(--mf-grad);color:#fff;display:grid;place-items:center;cursor:pointer;
  box-shadow:0 10px 24px rgba(0,0,0,.4);-webkit-tap-highlight-color:transparent;transition:transform .15s}
.mf-fab:active{transform:scale(.94)}
.mf-fab svg{font-size:26px}
.mf-fab-badge{position:absolute;top:-4px;right:-4px;min-width:20px;height:20px;padding:0 5px;border-radius:999px;background:#ef4444;color:#fff;
  font-size:11px;font-weight:800;display:grid;place-items:center;box-shadow:0 0 0 2px var(--mf-bg)}

/* ---------- Phones: posts use the full width of the screen ---------- */
@media (max-width:767px){
  .page.feed.mf-feed{--mf-pad:12px}
  .mf-topbar-inner{padding:8px 12px;gap:8px}
  .mf-brand{font-size:20px}
  .mf-logo{width:36px;height:36px;border-radius:11px}
  .mf-search{padding:10px 12px 0}
  .mf-widgets{padding:8px 12px 0;gap:12px}

  .mf-layout{padding:10px 0 100px;gap:0}
  .mf-main{gap:10px}
  .mf-main .mf-post{border-radius:0;border-left-width:0;border-right-width:0;box-shadow:none}
  .mf-main>.mf-skel{border-radius:0;border-left-width:0;border-right-width:0}
  .mf-main>.mf-empty{margin:0 12px}

  .mf-results{gap:10px}
  .mf-results>.mf-section-head,
  .mf-results>.mf-sub,
  .mf-results>.mf-user-row,
  .mf-results>.mf-empty{margin-left:12px;margin-right:12px}
}
@media (prefers-reduced-motion:reduce){
  .mf-feed *,.mf-feed *::before,.mf-feed *::after{animation:none!important;transition:none!important}
}
`;
injectStyles('mf-feed-css', FEED_CSS);

/* ------------------------------------------------------------------ */
/* Helpers + small components                                          */
/* ------------------------------------------------------------------ */
const displayName = (u) => (u?.name && u.name.trim()) || u?.username || 'Anon';

const SEGMENT_LABELS = {
  img: 'image', vid: 'video', aud: 'music', text: 'text', trends: 'trending',
};

function useMediaQuery(query) {
  const read = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(read);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

// a boolean remembered in localStorage (used for the collapsed side panels)
function usePersistedFlag(key, initial = false) {
  const [value, setValue] = useState(() => {
    try {
      const s = localStorage.getItem(key);
      return s === null ? initial : s === '1';
    } catch {
      return initial;
    }
  });
  const set = useCallback((next) => {
    setValue((prev) => {
      const val = typeof next === 'function' ? next(prev) : next;
      try { localStorage.setItem(key, val ? '1' : '0'); } catch {}
      return val;
    });
  }, [key]);
  return [value, set];
}

// subscribe without ever throwing / leaving an unhandled rejection (e.g. a collection that doesn't exist yet)
function subscribeSafe(name, handler, options) {
  try {
    const p = pb.collection(name).subscribe('*', handler, options);
    if (p && p.catch) p.catch(() => {});
    return p;
  } catch {
    return null;
  }
}

function unsubscribeSafe(u) {
  try {
    if (u && u.then) u.then((f) => f && f()).catch(() => {});
    else if (u) u();
  } catch {}
}

function BrandLogo() {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <span className="mf-logo mf-logo-fallback" aria-hidden="true">m</span>;
  }
  return (
    <img
      className="mf-logo"
      src={LOGO_SRC}
      alt=""
      width="40"
      height="40"
      onError={() => setFailed(true)}
    />
  );
}

// <img> that tries the CDN first, then falls back to PocketBase
function PartyImage({ record, field, alt }) {
  const [src, setSrc] = useState(getMediaUrl(record, record[field]));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSrc(getMediaUrl(record, record[field]));
    setFailed(false);
  }, [record, field]);

  return (
    <img
      src={src}
      alt={alt || ''}
      loading="lazy"
      onError={() => {
        if (failed) return;
        setFailed(true);
        setSrc(getPbUrl(record, record[field]));
      }}
    />
  );
}

function FeedSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div className="mf-skel" key={i} aria-hidden="true">
          <div className="mf-skel-row">
            <div className="mf-sk" style={{ width: 44, height: 44, borderRadius: '50%' }} />
            <div style={{ flex: 1, display: 'grid', gap: 8 }}>
              <div className="mf-sk" style={{ height: 12, width: '38%' }} />
              <div className="mf-sk" style={{ height: 10, width: '22%' }} />
            </div>
          </div>
          <div className="mf-sk" style={{ height: i === 1 ? 14 : 220 }} />
          <div className="mf-sk" style={{ height: 12, width: '70%' }} />
        </div>
      ))}
    </>
  );
}

function EmptyState({ icon = '✦', title, text, children, variant }) {
  return (
    <div className={`mf-empty${variant ? ` mf-empty--${variant}` : ''}`}>
      <div className="mf-empty-icon" aria-hidden="true">{icon}</div>
      <h3>{title}</h3>
      {text && <p>{text}</p>}
      {children}
    </div>
  );
}

// every media type a post contains (works for multi-item and older single-media posts)
function postTypes(post) {
  const set = new Set(getMediaItems(post).map((i) => i.type));
  const mt = String(post.mediaType || '').toLowerCase();
  if (mt === 'audio') set.add('music');
  else if (mt) set.add(mt);
  return set;
}

/* ------------------------------------------------------------------ */
/* Feed                                                                */
/* ------------------------------------------------------------------ */
export default function Feed() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const isWide = useMediaQuery(WIDE_QUERY);

  const [theme, toggleTheme] = useMfTheme();
  const rootRef = useRef(null);
  const topbarRef = useRef(null);
  const searchRef = useRef(null);
  const widgetsRef = useRef(null);
  const stripRef = useRef(null);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [segment, setSegment] = useState('default');

  // --- Side panels: collapsible into slim rails on desktop ---
  const [friendsCollapsed, setFriendsCollapsed] = usePersistedFlag('mf-friends-collapsed', false);
  const [partyCollapsed, setPartyCollapsed] = usePersistedFlag('mf-party-collapsed', false);

  // --- Friends state (phones: bottom sheet opened from the floating button) ---
  const [friendsOpen, setFriendsOpen] = useState(false);
  const [friendsBadge, setFriendsBadge] = useState(0);

  // --- Party state ---
  const [activePartyTab, setActivePartyTab] = useState('onraising');
  const [partyOpen, setPartyOpen] = useState(false); // only affects tablets (768 - 1199px)
  const [partyLoaded, setPartyLoaded] = useState(false);
  const [onRaisingList, setOnRaisingList] = useState([]);
  const [happeningList, setHappeningList] = useState([]);
  const [votesIndex, setVotesIndex] = useState({});
  const [userVotes, setUserVotes] = useState({});
  const [sheet, setSheet] = useState(null);          // { kind, id } on phones
  const [showWelcome, setShowWelcome] = useState(false);

  // --- Phone strip state ---
  const [stripVisible, setStripVisible] = useState(STRIP_FIRST);
  const [stripAway, setStripAway] = useState(false);
  const revealLock = useRef(false);
  const tabAutoPicked = useRef(false);

  // column widths shared by the layout grid and the widgets row above it
  const rootVars = isWide
    ? {
        '--mf-left-w': `${friendsCollapsed ? RAIL_W : FRIENDS_W}px`,
        '--mf-right-w': `${partyCollapsed ? RAIL_W : PARTY_W}px`,
      }
    : undefined;

  // While the Party column is collapsed, the like / reshare / comments / shop rail of the
  // centered post moves out of the cards and into that collapsed column (see <PostRail />)
  const railOutside = isWide && partyCollapsed;
  useEffect(() => {
    setRailOutside(railOutside);
    return () => setRailOutside(false);
  }, [railOutside]);

  /* ---------- Layout measurements ---------- */

  // the sticky top bar height (used by the post cards to fill the screen)
  useEffect(() => {
    const bar = topbarRef.current;
    const root = rootRef.current;
    if (!bar || !root) return undefined;
    const apply = () => root.style.setProperty('--mf-topbar-h', `${bar.offsetHeight}px`);
    apply();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', apply);
      return () => window.removeEventListener('resize', apply);
    }
    const ro = new ResizeObserver(apply);
    ro.observe(bar);
    return () => ro.disconnect();
  }, []);

  // phones: the strip starts right below the category tabs, so the search bar,
  // the Maya button and the tabs stay fully usable while the strip is visible
  useEffect(() => {
    const root = rootRef.current;
    const widgets = widgetsRef.current;
    if (!root || !widgets || !isMobile) return undefined;

    const measure = () => {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const first = widgets.firstElementChild;
      const useFirst = first && first.offsetHeight > 0;
      const rect = (useFirst ? first : widgets).getBoundingClientRect();
      const docY = (useFirst ? rect.bottom : rect.top) + scrollY;
      root.style.setProperty('--mf-strip-top', `${Math.max(0, Math.round(docY + 10))}px`);
    };

    measure();
    window.addEventListener('resize', measure);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      [topbarRef.current, searchRef.current, widgets].forEach((el) => { if (el) ro.observe(el); });
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [isMobile]);

  // phones: the strip only covers the screen at the top of the page
  useEffect(() => {
    if (!isMobile) return undefined;
    const onScroll = () => {
      const y = window.pageYOffset || document.documentElement.scrollTop || 0;
      setStripAway((prev) => (y > STRIP_HIDE_AT ? true : (STRIP_REAPPEARS ? false : prev)));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isMobile]);

  /* ---------- Data ---------- */
  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const list = await pb.collection('posts').getFullList({
        sort: '-created',
        expand: 'author',
        $autoCancel: false,
      });
      setPosts(list);
    } catch (err) {
      console.error(err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePostDeleted = useCallback((id) => {
    setPosts((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const loadPartiesAndVotes = async () => {
    try {
      const ors = await pb.collection('party_onraising')
        .getList(1, PARTY_FETCH, { sort: '-created', $autoCancel: false })
        .catch(() => ({ items: [] }));
      const hps = await pb.collection('party_happening')
        .getList(1, PARTY_FETCH, { sort: '-created', $autoCancel: false })
        .catch(() => ({ items: [] }));

      setOnRaisingList(ors.items || []);
      setHappeningList(hps.items || []);
      setPartyLoaded(true);

      const partyIds = [...(ors.items || []).map(i => i.id), ...(hps.items || []).map(i => i.id)];

      if (partyIds.length) {
        // PocketBase filters have no "in" operator, so build an OR chain instead
        const votes = await pb.collection('party_votes').getFullList({
          filter: partyIds.map((id) => `partyId="${id}"`).join(' || '),
          $autoCancel: false,
        }).catch(() => []);

        const idx = {};
        const uv = {};
        for (const v of votes) {
          idx[v.partyId] = idx[v.partyId] || { aye: 0, no: 0, voters: new Set() };
          if (v.vote === 'aye') idx[v.partyId].aye++;
          if (v.vote === 'no') idx[v.partyId].no++;
          idx[v.partyId].voters.add(v.userId);
          if (v.userId === user?.id) uv[v.partyId] = v.vote;
        }
        for (const k of Object.keys(idx)) idx[k].votersCount = idx[k].voters.size;
        setVotesIndex(idx);
        setUserVotes(uv);
      } else {
        setVotesIndex({});
        setUserVotes({});
      }
    } catch (err) {
      console.error('Failed loading party data', err);
    }
  };

  useEffect(() => {
    load();
    loadPartiesAndVotes();

    // Realtime posts: keep `expand.author` so new posts render correctly
    const unsubPosts = subscribeSafe('posts', (e) => {
      setPosts((prev) => {
        if (e.action === 'create') {
          return prev.some((p) => p.id === e.record.id) ? prev : [e.record, ...prev];
        }
        if (e.action === 'update') {
          return prev.map((p) =>
            p.id === e.record.id
              ? { ...e.record, expand: e.record.expand || p.expand }
              : p
          );
        }
        if (e.action === 'delete') return prev.filter((p) => p.id !== e.record.id);
        return prev;
      });
    }, { expand: 'author' });

    const unsubOr = subscribeSafe('party_onraising', () => loadPartiesAndVotes());
    const unsubHp = subscribeSafe('party_happening', () => loadPartiesAndVotes());
    const unsubVt = subscribeSafe('party_votes', () => loadPartiesAndVotes());

    return () => {
      [unsubPosts, unsubOr, unsubHp, unsubVt].forEach(unsubscribeSafe);
    };
    // Note: intentionally re-runs only when the logged-in user changes.
  }, [user?.id]);

  // 3s delayed popup
  useEffect(() => {
    const t = setTimeout(() => setShowWelcome(true), 3000);
    return () => clearTimeout(t);
  }, []);

  // if "On Raising" is empty but "Happening" isn't, start on "Happening" (once)
  useEffect(() => {
    if (tabAutoPicked.current || !partyLoaded) return;
    tabAutoPicked.current = true;
    if (!onRaisingList.length && happeningList.length) setActivePartyTab('happening');
  }, [partyLoaded, onRaisingList.length, happeningList.length]);

  /* ---------- Party sheet (phones) ---------- */
  const sheetItem = sheet
    ? (sheet.kind === 'onraising' ? onRaisingList : happeningList).find((i) => i.id === sheet.id)
    : null;

  useEffect(() => {
    if (!isMobile) setSheet(null);
  }, [isMobile]);

  useEffect(() => {
    if (sheet && !sheetItem) setSheet(null);
  }, [sheet, sheetItem]);

  useEffect(() => {
    if (!sheet) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setSheet(null); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [sheet]);

  /* ---------- Friends sheet (phones) ---------- */
  useEffect(() => {
    if (!isMobile) setFriendsOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!friendsOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setFriendsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [friendsOpen]);

  /* ---------- Party actions ---------- */
  const voteOnParty = async (partyId, vote) => {
    if (!user?.id) return alert('Login required to vote');
    if (!vote) return;
    try {
      const existing = await pb.collection('party_votes').getFirstListItem(
        `partyId="${partyId}" && userId="${user.id}"`,
      ).catch(() => null);

      if (existing) {
        if (existing.vote === vote) return;
        await pb.collection('party_votes').update(existing.id, { vote });
      } else {
        await pb.collection('party_votes').create({
          partyId, userId: user.id, vote, createdAt: new Date().toISOString(),
        });
      }
      setUserVotes((uv) => ({ ...uv, [partyId]: vote }));
      loadPartiesAndVotes();
    } catch (err) {
      console.error('Vote failed', err);
      alert('Failed to save vote.');
    }
  };

  const exceptParty = async (partyId) => {
    if (!user?.id) return alert('Login required');
    if (!window.confirm('Except this item?')) return;
    try {
      const update = async (collectionName) => {
        const rec = await pb.collection(collectionName).getOne(partyId).catch(() => null);
        if (!rec) return false;
        const newCount = (rec.exceptedCount || 0) + 1;
        await pb.collection(collectionName).update(partyId, { exceptedCount: newCount });
        return true;
      };
      const done = (await update('party_onraising')) || (await update('party_happening'));
      if (done) alert('Excepted!');
    } catch (err) {
      console.error('Except failed', err);
    }
  };

  const openPartyTab = (tab) => {
    setActivePartyTab(tab);
    setPartyOpen(true);
  };

  /* ---------- Filtering ---------- */
  const matchesSegment = (post, seg) => {
    if (!seg || seg === 'default') return true;
    const types = postTypes(post);
    if (seg === 'img') return types.has('image');
    if (seg === 'vid') return types.has('video');
    if (seg === 'aud') return types.has('music');
    if (seg === 'text') return types.has('text') || (types.size === 0 && !!(post.content || post.caption));
    if (seg === 'trends') return !!post.trending || !!post.isTrending || (post.trendingScore && post.trendingScore > 0);
    if (post.tags && Array.isArray(post.tags)) {
      return post.tags.includes(seg) || post.tags.some(t => String(t).toLowerCase() === seg);
    }
    return true;
  };

  const displayedPosts = (() => {
    if (searchResults) return null;
    if (!posts || posts.length === 0) return [];
    return posts.filter(p => matchesSegment(p, segment));
  })();

  /* ---------- Party cards (plain functions returning JSX, used by the
          desktop panel AND the phone bottom sheet) ---------- */
  const renderRaisingCard = (item) => {
    const v = votesIndex[item.id] || {};
    const mine = userVotes[item.id];
    return (
      <article className="mf-party-card" key={item.id}>
        <div className="mf-party-media">
          {item.thumbnail
            ? <PartyImage record={item} field="thumbnail" alt={item.title} />
            : <span>VIDEO</span>}
        </div>
        <div className="mf-party-info">
          <h3>{item.title || 'Untitled Challenge'}</h3>
          {item.description && <p>{item.description}</p>}
          <div className="mf-chips">
            <span className="mf-chip">✅ Excepted {item.exceptedCount || 0}</span>
            <span className="mf-chip">🗳 Voted {v.votersCount || 0}</span>
          </div>
          <div className="mf-party-actions">
            <button
              type="button"
              className={`mf-btn mf-btn--sm${mine === 'aye' ? ' is-on' : ''}`}
              aria-pressed={mine === 'aye'}
              onClick={() => voteOnParty(item.id, 'aye')}
            >👍 Aye · {v.aye || 0}</button>
            <button
              type="button"
              className={`mf-btn mf-btn--sm${mine === 'no' ? ' is-on' : ''}`}
              aria-pressed={mine === 'no'}
              onClick={() => voteOnParty(item.id, 'no')}
            >👎 No · {v.no || 0}</button>
            <button
              type="button"
              className="mf-btn mf-btn--sm mf-btn--primary"
              onClick={() => exceptParty(item.id)}
            >Except</button>
          </div>
        </div>
      </article>
    );
  };

  const renderHappeningCard = (item) => (
    <article className="mf-party-card" key={item.id}>
      <div className="mf-party-media">
        {item.image
          ? <PartyImage record={item} field="image" alt={item.title} />
          : <span>IMG</span>}
      </div>
      <div className="mf-party-info">
        <h3>{item.title || 'Party'}</h3>
        <div className="mf-meta-list">
          <span>👤 Host: {item.host || 'Unknown'}</span>
          <span>📍 {item.location || 'Location unavailable'}</span>
          <span>👥 Capacity: {item.headcount || '—'}</span>
        </div>
        {item.description && <p>{item.description}</p>}
        <div className="mf-party-actions">
          {!item.entryFee || item.entryFee === 0 ? (
            <button type="button" className="mf-btn mf-btn--sm mf-btn--primary">Join</button>
          ) : (
            <button
              type="button"
              className="mf-btn mf-btn--sm mf-btn--primary ticket-btn"
              onClick={() => item.entryUrl ? window.open(item.entryUrl, '_blank') : alert('No link yet')}
            >
              Buy Tickets
            </button>
          )}
        </div>
      </div>
    </article>
  );

  /* ---------- Party panel: desktop + tablet ---------- */
  const panelRaising = onRaisingList.slice(0, PANEL_MAX);
  const panelHappening = happeningList.slice(0, PANEL_MAX);
  const partyTotal = panelRaising.length + panelHappening.length;

  const partyPanel = (
    <aside className={`mf-party${partyOpen ? '' : ' is-collapsed'}`} aria-label="Party">
      <div className="mf-party-head">
        <h2 className="mf-party-title">
          <span aria-hidden="true">🎉</span> Party
          {partyTotal > 0 && <span className="mf-count">{partyTotal}</span>}
        </h2>

        <div className="mf-tabs" role="tablist" aria-label="Party sections">
          <button
            type="button"
            role="tab"
            aria-selected={activePartyTab === 'onraising'}
            className={`mf-tab${activePartyTab === 'onraising' ? ' is-active' : ''}`}
            onClick={() => openPartyTab('onraising')}
          >On Raising</button>
          <button
            type="button"
            role="tab"
            aria-selected={activePartyTab === 'happening'}
            className={`mf-tab${activePartyTab === 'happening' ? ' is-active' : ''}`}
            onClick={() => openPartyTab('happening')}
          >Happening</button>
        </div>

        {/* desktop: left arrow collapses the column into a rail */}
        {isWide && (
          <button
            type="button"
            className="mf-iconbtn"
            aria-label="Collapse party panel"
            title="Collapse"
            onClick={() => setPartyCollapsed(true)}
          >
            <ChevronLeftRoundedIcon />
          </button>
        )}

        {/* tablet: folds the card */}
        <button
          type="button"
          className="mf-party-toggle"
          aria-expanded={partyOpen}
          aria-label={partyOpen ? 'Collapse party panel' : 'Expand party panel'}
          onClick={() => setPartyOpen((o) => !o)}
        >
          <ExpandMoreRoundedIcon />
        </button>
      </div>

      <div className="mf-party-body">
        {activePartyTab === 'onraising' && (
          <div className="mf-party-list">
            {panelRaising.length === 0 && (
              <p className="mf-party-note">No challenges raising right now.</p>
            )}
            {panelRaising.map(renderRaisingCard)}
            <button type="button" className="mf-btn mf-btn--ghost mf-viewall" onClick={() => navigate('/on-raising')}>
              View all ➜
            </button>
          </div>
        )}

        {activePartyTab === 'happening' && (
          <div className="mf-party-list">
            {panelHappening.length === 0 && (
              <p className="mf-party-note">No parties happening right now.</p>
            )}
            {panelHappening.map(renderHappeningCard)}
            <button type="button" className="mf-btn mf-btn--ghost mf-viewall" onClick={() => navigate('/happening')}>
              View all ➜
            </button>
          </div>
        )}
      </div>
    </aside>
  );

  // collapsed Party column: slim rail, right arrow broadens it again
  const partyRail = (
    <aside className="mf-party-rail" aria-label="Party (collapsed)">
      <button
        type="button"
        className="mf-iconbtn"
        aria-label="Expand party panel"
        title="Expand"
        onClick={() => setPartyCollapsed(false)}
      >
        <ChevronRightRoundedIcon />
      </button>
      <span className="mf-rail-ico" aria-hidden="true">
        🎉
        {partyTotal > 0 && <span className="mf-rail-badge">{partyTotal > 9 ? '9+' : partyTotal}</span>}
      </span>
      <span className="mf-rail-label">Party</span>
      <PostRail />
    </aside>
  );

  /* ---------- Party strip: phones only ---------- */
  const stripKind = activePartyTab;
  const stripAll = (stripKind === 'onraising' ? onRaisingList : happeningList).slice(0, STRIP_MAX);
  const stripShown = stripAll.slice(0, stripVisible);

  // a different section starts again with the first 4
  useEffect(() => {
    setStripVisible(STRIP_FIRST);
    if (stripRef.current) stripRef.current.scrollTop = 0;
  }, [activePartyTab]);

  // scrolling to the end of the strip reveals the next batch (up to 20)
  const onStripScroll = (e) => {
    if (revealLock.current || stripVisible >= stripAll.length) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 140) {
      revealLock.current = true;
      setStripVisible((v) => Math.min(stripAll.length, v + STRIP_STEP));
      requestAnimationFrame(() => requestAnimationFrame(() => { revealLock.current = false; }));
    }
  };

  const showStrip = isMobile && !searchResults && (onRaisingList.length + happeningList.length) > 0;

  const partyStrip = (
    <aside
      ref={stripRef}
      className={`mf-strip${stripAway ? ' is-away' : ''}`}
      aria-label="Party"
      aria-hidden={stripAway}
      onScroll={onStripScroll}
    >
      <div className="mf-strip-tabs" role="tablist" aria-label="Party sections">
        <button
          type="button"
          role="tab"
          aria-selected={stripKind === 'onraising'}
          aria-label="On Raising"
          title="On Raising"
          className={stripKind === 'onraising' ? 'is-active' : ''}
          onClick={() => setActivePartyTab('onraising')}
        >🔥</button>
        <button
          type="button"
          role="tab"
          aria-selected={stripKind === 'happening'}
          aria-label="Happening"
          title="Happening"
          className={stripKind === 'happening' ? 'is-active' : ''}
          onClick={() => setActivePartyTab('happening')}
        >🎉</button>
      </div>

      {stripShown.length === 0 && <p className="mf-strip-empty">Nothing here yet</p>}

      {stripShown.map((item) => {
        const field = stripKind === 'onraising' ? 'thumbnail' : 'image';
        const label = item.title || (stripKind === 'onraising' ? 'Challenge' : 'Party');
        const initial = String(item.host || item.title || '?').trim()[0]?.toUpperCase() || '?';
        return (
          <button
            key={item.id}
            type="button"
            className="mf-strip-card"
            aria-label={`Open ${label}`}
            onClick={() => setSheet({ kind: stripKind, id: item.id })}
          >
            <span className="mf-strip-inner">
              {item[field]
                ? <PartyImage record={item} field={field} alt="" />
                : <span className="mf-strip-ph" aria-hidden="true">{stripKind === 'onraising' ? '🎬' : '🎉'}</span>}
              {stripKind === 'happening' && <span className="mf-strip-live"><i />NOW</span>}
              <span className="mf-strip-ava" aria-hidden="true">{initial}</span>
              <span className="mf-strip-label">{label}</span>
            </span>
          </button>
        );
      })}

      <button
        type="button"
        className="mf-strip-card mf-strip-more"
        onClick={() => navigate(stripKind === 'onraising' ? '/on-raising' : '/happening')}
        aria-label="View all"
      >
        <span className="mf-strip-inner">
          <StarRoundedIcon />
          <span>View all</span>
        </span>
      </button>
    </aside>
  );

  /* ---------- Main column content ---------- */
  const hasUsers = searchResults?.users?.length > 0;
  const hasPosts = searchResults?.posts?.length > 0;

  let mainContent;
  if (searchResults) {
    mainContent = (
      <section className="mf-results" aria-label="Search results">
        <div className="mf-section-head">
          <h2>Search results</h2>
          <button type="button" className="mf-btn mf-btn--sm mf-btn--ghost" onClick={() => setSearchResults(null)}>
            Clear
          </button>
        </div>

        {!hasUsers && !hasPosts && (
          <EmptyState icon="🔍" title="No results" text="Try a different name or keyword." />
        )}

        {hasUsers && (
          <>
            <h3 className="mf-sub">Users</h3>
            {searchResults.users.map((u) => (
              <Link key={u.id} to={`/profile/${u.id}`} className="mf-user-row">
                <Avatar user={u} size={44} />
                <div>
                  <strong>{displayName(u)}</strong>
                  {u.username && <span className="mf-handle">@{u.username}</span>}
                </div>
              </Link>
            ))}
          </>
        )}

        {hasPosts && (
          <>
            <h3 className="mf-sub">Posts</h3>
            {searchResults.posts.map((p) => (
              <PostCard key={p.id} post={p} onDeleted={handlePostDeleted} />
            ))}
          </>
        )}
      </section>
    );
  } else if (loading) {
    mainContent = <FeedSkeleton />;
  } else if (loadError) {
    mainContent = (
      <EmptyState
        variant="error"
        icon="⚠️"
        title="Couldn’t load your feed"
        text="Check your connection and try again."
      >
        <button type="button" className="mf-btn mf-btn--primary" onClick={() => { setLoading(true); load(); }}>
          Try again
        </button>
      </EmptyState>
    );
  } else if (displayedPosts && displayedPosts.length === 0) {
    mainContent = segment === 'default' ? (
      <EmptyState icon="✨" title="No posts yet" text="Be the first to share something with the community.">
        <Link to="/create" className="mf-btn mf-btn--primary">Create a post</Link>
      </EmptyState>
    ) : (
      <EmptyState
        icon="🗂"
        title={`No ${SEGMENT_LABELS[segment] || segment} posts yet`}
        text="Try another tab, or post something yourself."
      >
        <button type="button" className="mf-btn" onClick={() => setSegment('default')}>Show everything</button>
      </EmptyState>
    );
  } else {
    mainContent = displayedPosts.map((p) => (
      <PostCard key={p.id} post={p} onDeleted={handlePostDeleted} />
    ));
  }

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div
      ref={rootRef}
      className="page feed mf-feed"
      data-mf-theme={theme}
      style={rootVars}
    >
      <WelcomePopup show={showWelcome} onClose={() => setShowWelcome(false)} />

      <header className="mf-topbar" ref={topbarRef}>
        <div className="mf-topbar-inner">
          <h1 className="mf-brand">
            <Link to="/" onClick={scrollTop} aria-label="myFAVV home">
              <BrandLogo />
              <span className="mf-wordmark">my<b>FAVV</b></span>
            </Link>
          </h1>

          <nav className="mf-create-wrap" aria-label="Create">
            <Link to="/create" className="mf-create">
              <AddRoundedIcon />
              <span>Create</span>
            </Link>
          </nav>

          <Navbar theme={theme} onToggleTheme={toggleTheme} />
        </div>
      </header>

      <div className="mf-search maya-search-container" ref={searchRef}>
        <SearchBarWithMaya onResults={(res) => setSearchResults(res)} />
      </div>

      <div className="mf-widgets feed-top-widgets" ref={widgetsRef}>
        <div className="mf-segwrap">
          <SegmentTabs active={segment} onChange={(s) => setSegment(s)} />
        </div>
        <PopularChallenges />
        <BuzzingChallenges />
      </div>

      <div className="mf-layout">
        {/* Friends: left column on desktop (collapsible rail), collapsible card on tablets */}
        {!isMobile && (
          <aside className="mf-friends" aria-label="Friends">
            <FriendsPanel
              mode="sidebar"
              collapsible
              collapseDirection={isWide ? 'side' : 'vertical'}
              collapsed={isWide ? friendsCollapsed : undefined}
              onToggleCollapsed={() => setFriendsCollapsed((c) => !c)}
            />
          </aside>
        )}

        <main className="mf-main">{mainContent}</main>

        {!isMobile && (isWide && partyCollapsed ? partyRail : partyPanel)}
      </div>

      <StickyFooter />

      {showStrip && partyStrip}

      {/* Friends: phones get a floating button + bottom sheet */}
      {isMobile && (
        <>
          {!friendsOpen && (
            <button
              type="button"
              className="mf-fab"
              aria-label={friendsBadge > 0 ? `Open friends, ${friendsBadge} new requests` : 'Open friends'}
              onClick={() => setFriendsOpen(true)}
            >
              <PeopleAltRoundedIcon />
              {friendsBadge > 0 && <span className="mf-fab-badge">{friendsBadge > 9 ? '9+' : friendsBadge}</span>}
            </button>
          )}

          <div
            className={`mf-fsheet-overlay${friendsOpen ? ' is-open' : ''}`}
            role="presentation"
            onMouseDown={(e) => { if (e.target === e.currentTarget) setFriendsOpen(false); }}
          >
            <div className="mf-fsheet" role="dialog" aria-modal="true" aria-label="Friends">
              <span className="mf-fsheet-grip" aria-hidden="true" />
              <FriendsPanel
                mode="sidebar"
                onClose={() => setFriendsOpen(false)}
                onReceivedCount={setFriendsBadge}
              />
            </div>
          </div>
        </>
      )}

      {isMobile && sheet && sheetItem && (
        <div
          className="mf-sheet-overlay"
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setSheet(null); }}
        >
          <div
            className="mf-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={sheetItem.title || 'Party details'}
          >
            <div className="mf-sheet-head">
              <span className="mf-sheet-grip" aria-hidden="true" />
              <button type="button" className="mf-sheet-x" aria-label="Close" onClick={() => setSheet(null)}>
                <CloseRoundedIcon />
              </button>
            </div>
            <div className="mf-sheet-body">
              {sheet.kind === 'onraising' ? renderRaisingCard(sheetItem) : renderHappeningCard(sheetItem)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}